/**
 * Browser utilities — detection, language, global scope.
 *
 * Canonical implementations consolidated from:
 * - packages/ssm-web5/src/utils/browser.js
 * - packages/ssm-web5/src/utils/getLang.js
 * - apps/ssm-web4/src/app/utils/window-or-global.js
 * - apps/ssm-admin/src/utils/window-or-global.js
 * - apps/ssm-signup/src/utils.js  (getLang)
 */

/**
 * Check whether the browser uses the WebKit rendering engine.
 * @returns {boolean}
 */
export function isWebkit() {
    try {
        const styles = window.getComputedStyle(document.documentElement, '');
        const pre = (Array.prototype.slice
            .call(styles)
            .join('')
            .match(/-(moz|webkit|ms)-/) ||
            (styles.OLink === '' && ['', 'o']))[1];
        return pre === 'webkit';
    } catch {
        return false;
    }
}

const DEFAULT_LANG = 'en';
const FM_LANGS = ['fr', 'es', 'en', 'de', 'pt', 'it'];

/**
 * Detect the user's language from URL params or the browser's navigator,
 * falling back to `'en'` if not in the supported set.
 *
 * @returns {string} Two-letter language code (e.g. 'en', 'es', 'fr')
 */
export function getLang() {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (params?.lang) return params.lang;

    let userLang = navigator.language || navigator.userLanguage;
    userLang = userLang.substring(0, 2);
    if (FM_LANGS.includes(userLang)) return userLang;
    return DEFAULT_LANG;
}

/**
 * Universal global scope reference — works in browsers (`self`), Node.js
 * (`global`), and other environments.
 *
 * Replaces the duplicated `window-or-global.js` modules in web4 and admin.
 */
export const globalScope =
    (typeof self === 'object' && self.self === self && self) ||
    (typeof global === 'object' && global.global === global && global) ||
    this;
